import { ChangesToggle } from '@app/features/agent-changes/agent-changes';
import { useBlockEntityCommands } from '@app/features/next-soup/actions/use-block-entity-commands';
import {
  type BlockTool,
  ResponsiveBlockToolbar,
  ToolButton,
} from '@components/app/ResponsiveBlockToolbar';
import { useDrawerControl } from '@components/app/split-layout/components/SplitDrawerContext';
import type { FileOperation } from '@components/app/split-layout/components/SplitFileMenu';
import {
  SplitHeaderLeft,
  SplitHeaderRight,
} from '@components/app/split-layout/components/SplitHeader';
import { StaticSplitLabel } from '@components/app/split-layout/components/SplitLabel';
import { ProviderIcon } from '@core/component/AI/component/ProviderIcon';
import { Permissions } from '@core/component/SharePermissions';
import {
  ShareDialogContext,
  ShareModal,
  ShareTrigger,
} from '@core/component/TopBar/ShareButton';
import { isMobile } from '@core/mobile/isMobile';
import { openExternalUrl } from '@core/util/url';
import type { AgentSessionEntity } from '@entity';
import ArrowSquareOut from '@phosphor/arrow-square-out.svg';
import ChatCircleDots from '@phosphor/chat-circle-dots.svg';
import GitBranch from '@phosphor/git-branch.svg';
import ShareIcon from '@phosphor/share.svg';
import type { AgentSessionResponse } from '@service-agent-harness/generated/schemas';
import { createSignal, For, Show, Suspense } from 'solid-js';
import { useAgentSession } from '../context/AgentSessionContext';
import {
  ORIGIN_THREAD_DRAWER_ID,
  sessionOriginThread,
} from '../context/origin-thread';
import { AgentPullRequestChip } from './AgentPullRequestChip';
import {
  harnessTitle,
  sessionHarnessTitle,
  sessionRepositoryUrl,
} from './compose-agent-session-options';

export { harnessTitle, sessionRepositoryUrl };

/** Shared title precedence for standalone and workspace agent sessions. */
export function agentSessionTitle(
  session: AgentSessionResponse | undefined,
  transcriptTitle?: string | null
): string {
  const name = session?.name;
  if (name && name !== 'Agent Session') return name;
  return transcriptTitle ?? name ?? sessionHarnessTitle(session ?? {});
}

/**
 * Agent-session identity in the split header chrome plus the standard split
 * toolbar: static label, shared entity actions, the session's pull request
 * once one exists, and external-provider links.
 *
 * Rename lives on the title menu (channel / automation), not on a tap of
 * the name — `StaticSplitLabel` without `onRename` so a touch tap opens
 * the dropdown instead of an inline editor.
 */
export function AgentSplitHeader(props: {
  session: AgentSessionResponse | undefined;
  /** The fold's session title, preferred over the harness fallback. */
  title?: string;
}) {
  // The session, not `useBlockId()`: a block created from the launcher mounts
  // against a placeholder and keeps reporting it (see `Block.tsx`), so the
  // block id is the one thing here that is not a shareable session id.
  const { sessionId, metadata, userId } = useAgentSession();
  const conversation = useDrawerControl(ORIGIN_THREAD_DRAWER_ID);
  const title = () => agentSessionTitle(props.session, props.title);
  const permissions = () =>
    userId() && props.session?.ownerId === userId()
      ? Permissions.OWNER
      : props.session?.canEdit
        ? Permissions.CAN_EDIT
        : Permissions.CAN_VIEW;

  const entity = (): AgentSessionEntity | undefined => {
    const session = props.session;
    const id = sessionId();
    if (!session || !id) return undefined;
    return {
      type: 'agent_session',
      id,
      name: title(),
      ownerId: session.ownerId,
      botId: session.botId,
      status:
        session.status.kind === 'event'
          ? session.status.event
          : session.status.kind,
    };
  };
  useBlockEntityCommands({ resolveEntity: entity });
  const [shareOpen, setShareOpen] = createSignal(false);
  const shareContext = {
    isOpen: shareOpen,
    open: () => setShareOpen(true),
    close: () => setShareOpen(false),
  };

  const shareTools: BlockTool[] = [
    {
      label: 'Share',
      icon: ShareIcon,
      action: () => setShareOpen(true),
      condition: () => Boolean(entity()),
      buttonComponent: () => <ShareTrigger id={sessionId()} />,
    },
  ];

  const tools: BlockTool[] = [
    {
      label: 'Open conversation',
      icon: ChatCircleDots,
      action: () => conversation.toggle(),
      condition: () => sessionOriginThread(props.session) !== undefined,
    },
    {
      label: () => {
        const provider = props.session?.external?.provider;
        if (provider === 'claude-cloud') return 'Open in Claude';
        if (!provider) return 'Open externally';
        return `Open in ${provider.charAt(0).toUpperCase()}${provider.slice(1)}`;
      },
      icon: ArrowSquareOut,
      action: () => {
        const url = props.session?.external?.url;
        if (url) openExternalUrl(url);
      },
      condition: () => Boolean(props.session?.external?.url),
    },
  ];

  const openRepository: FileOperation = {
    label: 'Open repository',
    icon: GitBranch,
    action: () => {
      const url = sessionRepositoryUrl(props.session);
      if (url) openExternalUrl(url);
    },
  };
  const ops = (): FileOperation[] => [
    { op: 'rename' },
    { op: 'delete' },
    ...(sessionRepositoryUrl(props.session) ? [openRepository] : []),
  ];

  return (
    <ShareDialogContext.Provider value={shareContext}>
      <SplitHeaderLeft>
        <StaticSplitLabel
          icon={
            <ProviderIcon
              model={metadata()?.model ?? props.session?.model}
              class="size-4 shrink-0"
            />
          }
          label={title()}
        />
      </SplitHeaderLeft>

      {/* Tools live on the header row itself — `ResponsiveBlockToolbar`
          would push non-Share tools onto a second toolbar row. Markup
          mirrors its own header-tools branch; on mobile the tools collapse
          into the title menu via `menuTools` below instead. */}
      <SplitHeaderRight>
        <div class="order-[1000] flex items-center gap-1.5">
          <Show when={props.session?.pullRequestUrl}>
            {(url) => <AgentPullRequestChip url={url()} />}
          </Show>
          <Show when={!isMobile()}>
            <ChangesToggle />
            <For each={tools}>
              {(tool) => (
                <Show when={!tool.condition || tool.condition()}>
                  <ToolButton tool={tool} />
                </Show>
              )}
            </For>
          </Show>
        </div>
      </SplitHeaderRight>

      <Show when={entity()}>
        {(session) => (
          <Suspense>
            <ShareModal
              id={session().id}
              name={title()}
              owner={session().ownerId}
              itemType="agent_session"
              blockAlias="agent"
              userPermissions={permissions()}
              isSharePermOpen={shareOpen()}
              setIsSharePermOpen={setShareOpen}
            />
          </Suspense>
        )}
      </Show>

      <ResponsiveBlockToolbar
        tools={shareTools}
        menuTools={tools}
        ops={entity() ? ops() : []}
        id={sessionId() ?? ''}
        itemType="agent_session"
        entity={entity()}
        permissions={permissions()}
        name={title()}
      />
    </ShareDialogContext.Provider>
  );
}
